import dayjs from 'dayjs'
import 'dayjs/locale/it'

// Domain layer for "secondary assumptions" — the per-reminder tracking documents introduced by
// therapy-service specs/2026-09-assumption-response.md.
//
// Deliberately UI-free and framework-free: both the new-style pages (src/pages-new/) and the
// legacy ones (src/pages/) render this feature, and neither stack may import from the other
// (see the no-restricted-imports rule in .eslintrc.js). Everything that is a *rule* rather than
// a *rendering* lives here so the two stacks cannot drift apart on the parts that matter.

export const SECONDARY_ASSUMPTION_STATES = {
  PLANNED: 'PLANNED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  NOT_ANSWERED: 'NOT_ANSWERED'
} as const

export type SecondaryAssumptionState = typeof SECONDARY_ASSUMPTION_STATES[keyof typeof SECONDARY_ASSUMPTION_STATES]

export interface SecondaryAssumption {
  // RF3.1.1: the next dose not yet due is derived at read time and has no persisted document —
  // the API sends it with `_id: null`, first row of the first page, at most one per therapy.
  _id: string | null
  secondaryTherapyId: string
  drugName?: string
  expectedDate?: string
  state?: SecondaryAssumptionState
  takenAt?: string
  // a list since a single dose can be split across several sites; documents answered before that
  // change still carry a plain string — always read it through getBodyParts
  bodyPart?: string[] | string
  notificationSentAt?: string
  respondedAt?: string
  // therapy-service RF3.3: only on GET /secondary-assumptions/:id — the therapy's next dose, not yet
  // persisted, that the deep-link page lets the user move. null when it must not be moved from
  // this assumption (old link, therapy over, next dose already answered early).
  nextExpectedDate?: string | null
}

// RF4: the only two states a client may declare. PLANNED/NOT_ANSWERED are server-side only.
export interface SecondaryAssumptionResponseBody {
  state: typeof SECONDARY_ASSUMPTION_STATES.COMPLETED | typeof SECONDARY_ASSUMPTION_STATES.CANCELLED
  takenAt?: string
  bodyPart?: string[]
  // RF4.4: sent only when the user actually moved the next dose — see isNextDoseEdited
  nextExpectedDate?: string
  // RF4.4: the next dose as the form showed it, sent along with a moved one so therapy-service can
  // ignore a move made from a stale form
  currentNextExpectedDate?: string
}

export const SECONDARY_ASSUMPTION_STATE_LABELS: Record<string, string> = {
  [SECONDARY_ASSUMPTION_STATES.PLANNED]: 'Da confermare',
  [SECONDARY_ASSUMPTION_STATES.COMPLETED]: 'Assunta',
  [SECONDARY_ASSUMPTION_STATES.CANCELLED]: 'Non assunta',
  [SECONDARY_ASSUMPTION_STATES.NOT_ANSWERED]: 'Nessuna risposta'
}

// Kept as semantic names rather than colors so each stack can map them onto its own palette
// (StatusPill in the new style, Chip colors in the legacy one) without sharing a theme.
export const SECONDARY_ASSUMPTION_STATE_TONES: Record<string, 'warning' | 'success' | 'error' | 'default'> = {
  [SECONDARY_ASSUMPTION_STATES.PLANNED]: 'warning',
  [SECONDARY_ASSUMPTION_STATES.COMPLETED]: 'success',
  [SECONDARY_ASSUMPTION_STATES.CANCELLED]: 'error',
  [SECONDARY_ASSUMPTION_STATES.NOT_ANSWERED]: 'default'
}

export function formatSecondaryAssumptionState(state?: string): string {
  return state ? SECONDARY_ASSUMPTION_STATE_LABELS[state] ?? state : '—'
}

export function getSecondaryAssumptionStateTone(state?: string): 'warning' | 'success' | 'error' | 'default' {
  return state ? SECONDARY_ASSUMPTION_STATE_TONES[state] ?? 'default' : 'default'
}

// A PLANNED row whose time is still ahead is the next dose, scheduled — not yet a dose "waiting
// for an answer". The diary shows these apart from the history (see NextDosesCard), so the table
// only ever lists doses whose time has come.
export function isUpcomingSecondaryAssumption(assumption?: SecondaryAssumption, now: dayjs.Dayjs = dayjs()): boolean {
  return assumption?.state === SECONDARY_ASSUMPTION_STATES.PLANNED &&
    Boolean(assumption?.expectedDate) && dayjs(assumption?.expectedDate).isAfter(now)
}

// RF4.2: there is no deadline and no single use — an assumption stays answerable (and
// correctable) forever, including one already closed as NOT_ANSWERED because no answer ever
// arrived. So this is intentionally always true; it exists to give the rule one name and one
// place, instead of leaving each call site to re-derive "is this row still actionable?" and
// quietly reintroduce an expiry the API does not have.
export function canRespondToSecondaryAssumption(_assumption?: SecondaryAssumption): boolean {
  return true
}

export function isSecondaryAssumptionAnswered(assumption?: SecondaryAssumption): boolean {
  return assumption?.state === SECONDARY_ASSUMPTION_STATES.COMPLETED ||
    assumption?.state === SECONDARY_ASSUMPTION_STATES.CANCELLED
}

// RF1.4/RF3.1.1: the derived row for the next dose is never a persisted document, and is
// answered by natural key (RF4.3) instead of by _id (RF4). This is the one place that decides
// which addressing a caller must use — everywhere that responds to an assumption should branch
// on this rather than re-deriving "is _id present?" locally.
export function isDerivedSecondaryAssumption(assumption?: SecondaryAssumption): boolean {
  return Boolean(assumption) && assumption?._id == null
}

// Stable identity for a row that may not have an _id yet — for React `key`s and for matching a
// row after answering it turns it from derived into persisted (its `_id` changes from null to a
// real one, so matching on `_id` alone would silently drop the update).
export function getSecondaryAssumptionKey(assumption: SecondaryAssumption): string {
  return assumption._id ?? `${assumption.secondaryTherapyId}:${assumption.expectedDate ?? ''}`
}

// --- "Al bisogno" ----------------------------------------------------------------------------
//
// therapy-service specs/2026-10-as-needed-assumptions.md: the patient records a dose "al bisogno"
// of any drug of the catalog — psp-backend hangs it on the patient's "al bisogno" therapy for that
// drug (updating it with notes, dosage and route), creating it when there is none.

export interface AsNeededDoseForm {
  drugId: string
  date: string | null
  // optional: without it the dose is recorded at the start of `date`
  time: string | null
  bodyPart: string[]
  notes: string
  // dosage and route of the dose: psp-backend stores them on the therapy, not on the dose
  assumptionDosage: {value?: number; unitType?: string}
  modeOfAssumption: string
}

// The day of `date` at the hour/minute of `time` (00:00 without it), truncated to the minute.
export function combineDateAndTime(date: string | null, time: string | null): string | null {
  if (!date || !dayjs(date).isValid()) return null
  const day = dayjs(date).startOf('day')
  if (!time || !dayjs(time).isValid()) return day.toISOString()
  return day.hour(dayjs(time).hour()).minute(dayjs(time).minute()).toISOString()
}

// RF3.2, checked before sending: why the form cannot be sent, or undefined.
export function validateAsNeededDose(form: AsNeededDoseForm, now: dayjs.Dayjs = dayjs()): string | undefined {
  if (!form.drugId) return 'Scegli il farmaco'
  const takenAt = combineDateAndTime(form.date, form.time)
  if (!takenAt) return 'Indica la data dell\'assunzione'
  if (dayjs(takenAt).isAfter(now)) return 'L\'assunzione non può essere nel futuro'
  return undefined
}

// --- Response form ---------------------------------------------------------------------------
//
// The two stacks render this form with different component libraries, but the rules behind it are
// identical and subtle enough that duplicating them would guarantee divergence. They live here;
// only the rendering is written twice.

export type AssumptionTakenChoice = 'yes' | 'no'

export interface SecondaryAssumptionResponseForm {
  taken: AssumptionTakenChoice | null
  takenAt: string | null
  bodyPart: string[]
  // RF3.3/RF4.4: present only when the assumption came with a movable next dose (deep-link page)
  nextExpectedDate?: string | null
}

// Free text by design (the spec rules out a closed enum), but offering the usual answers keeps the
// data comparable and spares the user typing on a phone. "Altro" falls back to a text field.
export const BODY_PART_SUGGESTIONS = [
  'Braccio sinistro',
  'Braccio destro',
  'Coscia sinistra',
  'Coscia destra',
  'Addome',
  'Gluteo sinistro',
  'Gluteo destro'
]

// therapyDiaryConfigurations.secondaryTherapiesConfiguration.bodyParts lets a PSP replace the
// shortcuts above with its own list (configured in the backoffice under Diario paziente > Terapie
// accessorie). Blank entries are dropped; a PSP that never sets it, or sets an empty list, keeps
// the defaults.
export function getBodyPartSuggestions(configuredBodyParts?: string[]): string[] {
  const configured = (configuredBodyParts ?? []).map(bodyPart => bodyPart?.trim()).filter(Boolean)
  return configured.length > 0 ? configured : BODY_PART_SUGGESTIONS
}

// bodyPart as a list whatever shape the document carries: assumptions answered before the field
// became multi-select still hold a single string.
export function getBodyParts(assumption?: SecondaryAssumption): string[] {
  const bodyPart = assumption?.bodyPart
  if (!bodyPart) { return [] }
  return Array.isArray(bodyPart) ? bodyPart : [bodyPart]
}

export function formatBodyParts(assumption?: SecondaryAssumption): string {
  return getBodyParts(assumption).join(', ')
}

// Multi-select picker rules, shared by both stacks: every shortcut toggles on its own, and the
// free-text "Altro" entry sits next to them as one more value — whatever is not a shortcut.
export function toggleBodyPart(selected: string[], bodyPart: string): string[] {
  return selected.includes(bodyPart) ? selected.filter(item => item !== bodyPart) : [...selected, bodyPart]
}

export function getOtherBodyPart(selected: string[], suggestions: string[]): string {
  return selected.filter(item => !suggestions.includes(item)).join(', ')
}

export function setOtherBodyPart(selected: string[], suggestions: string[], other: string): string[] {
  const shortcuts = selected.filter(item => suggestions.includes(item))
  return other ? [...shortcuts, other] : shortcuts
}

// Precompiles the form from an assumption that was already answered, so reopening the link — from
// the second channel of the same send, or much later to correct it — shows the previous answer
// instead of an empty form (RF3/CA3). `takenAt` always defaults to expectedDate when there is no
// answer (or no divergent one) yet, since the date/time fields are always shown and need a starting
// value — the user is almost always nudging that value, not entering one from scratch.
export function getInitialResponseForm(assumption?: SecondaryAssumption): SecondaryAssumptionResponseForm {
  // RF3.3: the next dose starts from what the server derived, whatever the answer state
  const nextDose = assumption?.nextExpectedDate ? {nextExpectedDate: assumption.nextExpectedDate} : {}

  if (!assumption || !isSecondaryAssumptionAnswered(assumption)) {
    return {taken: null, takenAt: assumption?.expectedDate ?? null, bodyPart: [], ...nextDose}
  }

  if (assumption.state === SECONDARY_ASSUMPTION_STATES.CANCELLED) {
    return {taken: 'no', takenAt: null, bodyPart: [], ...nextDose}
  }

  return {
    taken: 'yes',
    takenAt: assumption.takenAt ?? assumption.expectedDate ?? null,
    bodyPart: getBodyParts(assumption),
    ...nextDose
  }
}

// RF4.4: whether the user moved the next dose away from what the server sent. Compared at the
// minute, like the server does, so a round trip through the pickers is not an edit.
//
// This is also why an unedited next dose is never sent back, even though the server would accept
// it as a no-op: if the user only changes takenAt, therapy-service may shift the schedule from it
// on its own (RF6, asynchronously). The next dose still shown here is then stale, and sending it
// back on a later "Aggiorna" would read as an explicit edit and undo that shift.
export function isNextDoseEdited(form: SecondaryAssumptionResponseForm, assumption?: SecondaryAssumption): boolean {
  if (!form.nextExpectedDate || !assumption?.nextExpectedDate) { return false }
  return !dayjs(form.nextExpectedDate).isSame(dayjs(assumption.nextExpectedDate), 'minute')
}

// Moving the dose being answered drags the next one along by the same amount, so the interval
// between the two stays what the therapy prescribes. Callers stop applying it as soon as the
// patient edits the next dose by hand: from then on their value wins.
export function followTakenAt(
  previous: SecondaryAssumptionResponseForm,
  next: SecondaryAssumptionResponseForm
): SecondaryAssumptionResponseForm {
  if (!previous.takenAt || !next.takenAt || !next.nextExpectedDate) { return next }
  const from = dayjs(previous.takenAt)
  const to = dayjs(next.takenAt)
  const nextDose = dayjs(next.nextExpectedDate)
  if (!from.isValid() || !to.isValid() || !nextDose.isValid() || from.isSame(to, 'minute')) { return next }
  return {...next, nextExpectedDate: nextDose.add(to.diff(from, 'minute'), 'minute').toISOString()}
}

// An edited next dose must be a real date in the future — the server answers 400 otherwise and
// saves nothing, not even the answer (RF4.4).
export function isNextDoseValid(form: SecondaryAssumptionResponseForm, assumption?: SecondaryAssumption): boolean {
  if (!isNextDoseEdited(form, assumption)) { return true }
  const next = dayjs(form.nextExpectedDate as string)
  return next.isValid() && next.isAfter(dayjs())
}

// therapy-service RF4.5: the derived "next dose" row can be moved from the diary without
// answering it — the way a patient who never gets the reminder link (reminders off, no channels)
// can still move it. Only while it is still ahead: a derived row already in the past is a dose
// just becoming due, about to be persisted by the server.
export function canMoveSecondaryAssumption(assumption?: SecondaryAssumption): boolean {
  return isDerivedSecondaryAssumption(assumption) &&
    Boolean(assumption?.expectedDate) && dayjs(assumption?.expectedDate).isAfter(dayjs())
}

// A move is worth sending only to a valid, future time actually different (at the minute, as the
// server compares) from the current one.
export function isNextDoseMoveValid(value: string | null, current?: string): boolean {
  if (!value) { return false }
  const next = dayjs(value)
  return next.isValid() && next.isAfter(dayjs()) && !next.isSame(dayjs(current), 'minute')
}

export function isResponseFormValid(form: SecondaryAssumptionResponseForm, assumption?: SecondaryAssumption): boolean {
  if (form.taken === null) { return false }
  if (!isNextDoseValid(form, assumption)) { return false }
  if (form.taken === 'no') { return true }
  return Boolean(form.takenAt) && !Number.isNaN(new Date(form.takenAt as string).getTime())
}

// bodyPart is not sent with CANCELLED: a body part for a dose that was not taken carries no
// meaning, and the server would persist it as-is.
//
// `assumption` is the document the form was opened on: it is what tells an edited next dose from
// an untouched one (see isNextDoseEdited). Callers without a movable next dose can omit it.
export function buildSecondaryAssumptionResponseBody(
  form: SecondaryAssumptionResponseForm,
  assumption?: SecondaryAssumption
): SecondaryAssumptionResponseBody {
  const nextDose = isNextDoseEdited(form, assumption)
    ? {
        nextExpectedDate: new Date(form.nextExpectedDate as string).toISOString(),
        currentNextExpectedDate: assumption?.nextExpectedDate as string
      }
    : {}

  if (form.taken === 'no') {
    return {state: SECONDARY_ASSUMPTION_STATES.CANCELLED, ...nextDose}
  }

  // trimmed, blank entries (an empty "Altro" field) dropped, duplicates collapsed
  const bodyParts = Array.from(new Set(form.bodyPart.map(bodyPart => bodyPart.trim()).filter(Boolean)))

  return {
    state: SECONDARY_ASSUMPTION_STATES.COMPLETED,
    ...form.takenAt ? {takenAt: new Date(form.takenAt).toISOString()} : {},
    ...bodyParts.length > 0 ? {bodyPart: bodyParts} : {},
    ...nextDose
  }
}

// A therapy that has never had a reminder sent has no assumptions, and the API answers 404 rather
// than an empty array. That is not a failure to report: the list's own empty state already says
// there is nothing, and a popup on top of it tells the user something broke when nothing did.
//
// Shared between the two stacks because it decides whether an error is shown at all, and the two
// portals must not disagree on that. Note the trade-off: a route that is genuinely missing (not
// deployed yet) also answers 404 and is therefore reported as "no reminders" — acceptable because
// the empty list is the far more common case and the alternative cries wolf on every empty therapy.
export function isEmptyListStatus(httpStatus?: number): boolean {
  return httpStatus === 404
}

// --- PSP-driven field visibility --------------------------------------------------------------
//
// A PSP declares which fields of an assumption it cares about as a list of columns (same shape used
// elsewhere in the app for PSP-driven table columns: {id, label, type, format}). Two places can
// hold that list — see getAssumptionColumnOptions. A PSP that never sets either sees every field,
// exactly as before this config existed — only an explicit, non-empty list narrows what is shown.

export interface AssumptionColumnConfig {
  id: string
  label?: string
  type?: string
  format?: string
}

const DEFAULT_ASSUMPTION_FIELD_IDS = ['date', 'time', 'bodyPart', 'status']

// The backoffice configurator (Diario paziente > Terapie accessorie > Colonne) shares its column
// ids with the primary therapies diary, so it saves 'hours' and 'state' where this module says
// 'time' and 'status'. Normalized here, once, so every caller can keep using the ids above.
const ASSUMPTION_COLUMN_ID_ALIASES: Record<string, string> = {
  hours: 'time',
  state: 'status'
}

function normalizeAssumptionColumnId(id: string): string {
  return ASSUMPTION_COLUMN_ID_ALIASES[id] ?? id
}

// therapyDiaryConfigurations.secondaryTherapiesConfiguration.columns is what the backoffice
// configurator writes; therapyConfigurations.secondaryTherapiesConfiguration.assumptionsColumnOptions
// is the older place, never written by the configurator but possibly set by hand on some PSP.
// The configurator wins when it has a non-empty list. Returns the stored array itself (not a copy)
// so it is safe to use directly as a useAppSelector result.
export function getAssumptionColumnOptions(pspConfig?: Record<string, any>): AssumptionColumnConfig[] | undefined {
  const diaryColumns = pspConfig?.therapyDiaryConfigurations?.secondaryTherapiesConfiguration?.columns
  if (Array.isArray(diaryColumns) && diaryColumns.length > 0) { return diaryColumns }
  return pspConfig?.therapyConfigurations?.secondaryTherapiesConfiguration?.assumptionsColumnOptions
}

export function getVisibleAssumptionFieldIds(columnOptions?: AssumptionColumnConfig[]): Set<string> {
  const activeIds = columnOptions && columnOptions.length > 0
    ? columnOptions.map(col => normalizeAssumptionColumnId(col.id))
    : DEFAULT_ASSUMPTION_FIELD_IDS
  return new Set(activeIds)
}

// Columns of the "Terapie accessorie" diary table, in the order and with the labels the PSP
// configured. Without a config the table keeps the columns it always had — body part included only
// when a PSP asks for it, since most rows have none.
export interface AssumptionTableColumn {
  id: string
  label: string
}

const DEFAULT_ASSUMPTION_TABLE_COLUMNS: AssumptionTableColumn[] = [
  {id: 'date', label: 'Data'},
  {id: 'time', label: 'Ora'},
  {id: 'drug', label: 'Farmaco'},
  {id: 'status', label: 'Stato'}
]

const ASSUMPTION_TABLE_COLUMN_IDS = new Set(['date', 'time', 'drug', 'status', 'bodyPart'])

export function getAssumptionTableColumns(columnOptions?: AssumptionColumnConfig[]): AssumptionTableColumn[] {
  if (!columnOptions || columnOptions.length === 0) { return DEFAULT_ASSUMPTION_TABLE_COLUMNS }
  const defaultLabels = new Map(DEFAULT_ASSUMPTION_TABLE_COLUMNS.map(col => [col.id, col.label]))
  return columnOptions
    .map(col => ({id: normalizeAssumptionColumnId(col.id), label: col.label}))
    .filter(col => ASSUMPTION_TABLE_COLUMN_IDS.has(col.id))
    .map(col => ({id: col.id, label: col.label || defaultLabels.get(col.id) || 'Parte del corpo'}))
}

// --- Formatting ------------------------------------------------------------------------------
//
// Shared rather than duplicated per stack: these strings are what the user reads to decide whether
// a dose was taken on time, so the two portals must not word or round them differently.

export function formatAssumptionDate(date?: string): string {
  return date ? dayjs(date).locale('it').format('DD/MM/YYYY') : ''
}

export function formatAssumptionTime(date?: string): string {
  return date ? dayjs(date).locale('it').format('HH:mm') : ''
}

export function formatAssumptionDateTime(date?: string): string {
  return date ? dayjs(date).locale('it').format('DD/MM/YYYY HH:mm') : ''
}

// "oggi alle 17:46" / "domani alle 15:46" / "sab 17 ottobre alle 09:00": the next dose is read as
// "when do I have to take it", so relative days where they apply.
export function formatNextDoseWhen(date?: string, now: dayjs.Dayjs = dayjs()): string {
  if (!date) { return '' }
  const when = dayjs(date).locale('it')
  const today = now.startOf('day')
  const day = when.isSame(today, 'day')
    ? 'oggi'
    : when.isSame(today.add(1, 'day'), 'day') ? 'domani' : when.format('ddd DD MMMM')
  return `${day} alle ${when.format('HH:mm')}`
}

// One row of the assumptions list only has something to add beyond its time badge and state pill
// when the actual outcome diverges from the plan — a different time, or a body part. Both are the
// exception, not the rule (scenario 3 of RF5.1, "taken as planned", is the common case and adds
// neither), so this returns only the parts that are actually informative instead of always
// reserving space for labeled fields that are usually empty ("Parte del corpo: —" on every row
// that has none is noise, not information).
export function getAssumptionSummaryParts(
  assumption?: SecondaryAssumption,
  visibleFields: Set<string> = new Set(DEFAULT_ASSUMPTION_FIELD_IDS)
): string[] {
  if (!assumption) { return [] }

  const parts: string[] = []
  const showTiming = visibleFields.has('date') || visibleFields.has('time')

  if (showTiming && assumption.takenAt && assumption.takenAt !== assumption.expectedDate) {
    parts.push(`Assunta il ${formatAssumptionDateTime(assumption.takenAt)}`)
  }
  if (visibleFields.has('bodyPart') && getBodyParts(assumption).length > 0) {
    parts.push(formatBodyParts(assumption))
  }

  return parts
}

// --- Day-strip navigation ----------------------------------------------------------------------
//
// RF3.1 has no date filter — only secondaryTherapyId/state and _l/_sk pagination, sorted reverse
// chronologically. So there is no way to ask "give me this calendar day's reminders" from the
// server; a day-strip navigator can only work over a WINDOW of already-fetched documents, grouped
// by calendar day client-side. `ASSUMPTIONS_WINDOW_SIZE` bounds that window: it is a document
// count, not a day count, so it covers many months for an infrequent therapy and only a handful of
// days for an hourly one — there is no fixed "last N days" guarantee, only "last N reminders".
export const ASSUMPTIONS_WINDOW_SIZE = 90

// Local time (not UTC), like every other formatter here: a reminder sent at 23:50 local belongs to
// that evening's day, not to whatever day it would roll into in UTC.
export function getAssumptionDayKey(assumption?: SecondaryAssumption): string | undefined {
  return assumption?.expectedDate ? dayjs(assumption.expectedDate).format('YYYY-MM-DD') : undefined
}

export function getTodayDayKey(): string {
  return dayjs().format('YYYY-MM-DD')
}

export function formatDayStripLabel(dayKey: string): {dayNumber: string; weekday: string} {
  const parsed = dayjs(dayKey, 'YYYY-MM-DD').locale('it')
  return {dayNumber: parsed.format('D'), weekday: parsed.format('ddd')}
}

// Distinct calendar days actually present in a batch of assumptions, oldest first — the order the
// day-strip renders left to right, ending on the most recent day (there is no future data: a
// PLANNED assumption's expectedDate is the dose the reminder that was just sent is about, not a
// forecast — see RF1.1).
export function getDistinctAssumptionDays(assumptions: SecondaryAssumption[]): string[] {
  const keys = new Set<string>()
  assumptions.forEach((assumption) => {
    const key = getAssumptionDayKey(assumption)
    if (key) { keys.add(key) }
  })
  return Array.from(keys).sort()
}

// The day-strip opens on today when today actually has a reminder; otherwise on the most recent
// day that does (the rightmost chip) — never on the oldest, which would bury what just happened
// under months of history on first open.
export function pickDefaultAssumptionDay(days: string[]): string | undefined {
  if (days.length === 0) { return undefined }
  const today = getTodayDayKey()
  return days.includes(today) ? today : days[days.length - 1]
}
